import { StrictMode } from "react";
import { createRoot, Root } from "react-dom/client";
import Widget from "./widget";
import { widgetCss } from "./styles";
import { getCompanyInfo } from "./api/company";
import { isHexColor, readableTextColor } from "./utils/color";

const DEFAULT_PRIMARY = "#565e74";

/** Curated Google-Fonts-presets (Instellingen > Widget & huisstijl) — bewust geen vrij
 * tekstveld, om typefouten/onleesbare fonts te voorkomen. Keys moeten exact overeenkomen
 * met CompanyProfileService.WIDGET_FONTS op de backend. */
const FONT_PRESETS: Record<string, { stack: string; importUrl: string }> = {
    inter: {
        stack: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        importUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
    },
    poppins: {
        stack: "Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        importUrl: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
    },
    playfair: {
        stack: "'Playfair Display', ui-serif, Georgia, serif",
        importUrl: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap",
    },
    roboto: {
        stack: "Roboto, ui-sans-serif, system-ui, -apple-system, Segoe UI, Arial",
        importUrl: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap",
    },
    nunito: {
        stack: "Nunito, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        importUrl: "https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&display=swap",
    },
};
const DEFAULT_FONT_PRESET = "inter";

/** Vertaalt de "look"-preset (Instellingen > Widget & huisstijl) naar de daadwerkelijke
 * randius-tokens van de widget. Een expliciet radius/fab-radius-attribuut wint altijd
 * hierop, net als bij de kleur. */
function shapeToRadii(shape: string | null): { radius: string; fabRadius: string } {
    if (shape === "square") return { radius: "10px", fabRadius: "14px" };
    return { radius: "20px", fabRadius: "999px" }; // "rounded" (default)
}

class BookitWidgetElement extends HTMLElement {
    private shadow: ShadowRoot;
    private mountEl: HTMLDivElement;
    private reactRoot: Root | null = null;

    // Huisstijl die is opgehaald via de company-slug (Mijn agenda > Widget & huisstijl
    // in het dashboard). Wordt alleen gebruikt voor de eigenschappen waarvoor geen
    // expliciet attribuut is meegegeven — een expliciet attribuut wint altijd.
    private fetchedBrandColor: string | null = null;
    private fetchedWidgetPosition: string | null = null;
    private fetchedWidgetShape: string | null = null;
    private fetchedWidgetButtonText: string | null = null;
    private fetchedWidgetBackground: string | null = null;
    private fetchedWidgetFont: string | null = null;
    private fetchedWidgetMode: string | null = null;
    private styleFetchedFor: string | null = null;

    static get observedAttributes() {
        return [
            "company",
            "mode",
            "api-base",
            "lang",
            "primary",
            "accent",
            "background",
            "panel",
            "text",
            "muted",
            "border",
            "font",
            "button-text",
            "radius",
            "width",
            "shape",
            "position",
            "offset-x",
            "offset-y",
            "fab-radius",
            "fab-width",
            "fab-height",
            "fab-padding",
            "button-text-color",
            "captcha-token",
        ];
    }

    constructor() {
        super();

        this.shadow = this.attachShadow({ mode: "open" });

        const style = document.createElement("style");
        style.textContent = this.buildCss();
        this.shadow.appendChild(style);

        this.mountEl = document.createElement("div");
        this.shadow.appendChild(this.mountEl);
    }

    connectedCallback() {
        this.renderReact();
        this.maybeFetchCompanyStyle();
    }

    disconnectedCallback() {
        this.reactRoot?.unmount();
        this.reactRoot = null;
    }

    attributeChangedCallback() {
        this.updateStyle();
        this.renderReact();
        this.maybeFetchCompanyStyle();
    }

    /** Effectieve merkkleur: expliciet attribuut wint, anders de kleur uit de
     * company-instellingen (indien al opgehaald), anders de widget-default. */
    private effectivePrimary(): string {
        const explicit = this.getAttribute("primary");
        if (explicit) return explicit;
        if (this.fetchedBrandColor) return this.fetchedBrandColor;
        return DEFAULT_PRIMARY;
    }

    /** Zelfde precedentie als effectivePrimary(), voor de hoek waarin de widget verschijnt. */
    private effectivePosition(): "bottom-right" | "bottom-left" | "top-right" | "top-left" {
        const explicit = this.getAttribute("position");
        if (explicit === "bottom-right" || explicit === "bottom-left" || explicit === "top-right" || explicit === "top-left") {
            return explicit;
        }
        if (this.fetchedWidgetPosition === "bottom-right" || this.fetchedWidgetPosition === "bottom-left" || this.fetchedWidgetPosition === "top-right" || this.fetchedWidgetPosition === "top-left") {
            return this.fetchedWidgetPosition;
        }
        return "bottom-right";
    }

    /** Zelfde precedentie, voor de vormtaal (rounded/square) — bepaalt radius + fab-radius
     * tenzij die expliciet als los attribuut zijn meegegeven. */
    private effectiveShape(): string | null {
        return this.getAttribute("radius") || this.getAttribute("fab-radius")
            ? null // los radius/fab-radius attribuut aanwezig: shape-preset niet toepassen
            : this.getAttribute("shape") || this.fetchedWidgetShape;
    }

    /** Zelfde precedentie, voor de knoptekst. */
    private effectiveButtonText(): string {
        const explicit = this.getAttribute("button-text");
        if (explicit) return explicit;
        if (this.fetchedWidgetButtonText) return this.fetchedWidgetButtonText;
        return "Boek nu";
    }

    /** Zelfde precedentie, voor de achtergrondkleur van de widget. */
    private effectiveBackground(): string {
        const explicit = this.getAttribute("background");
        if (isHexColor(explicit)) return explicit;
        if (isHexColor(this.fetchedWidgetBackground)) return this.fetchedWidgetBackground;
        return "#f7f9fb";
    }

    /** Leidt een leesbare tekstkleur af zodra de achtergrond aangepast is — alleen
     * gebruikt als er geen expliciete "text"-kleur is meegegeven. */
    private autoTextColor(): string {
        return readableTextColor(this.effectiveBackground());
    }

    /** Zelfde precedentie, voor het curated lettertype-preset. Een expliciet "font"-
     * attribuut wordt als vrije CSS font-family-waarde gebruikt (geen preset, geen
     * @import) — de curated presets gelden alleen zolang er geen los font-attribuut is. */
    private effectiveFontPresetKey(): string {
        if (this.fetchedWidgetFont && FONT_PRESETS[this.fetchedWidgetFont]) return this.fetchedWidgetFont;
        return DEFAULT_FONT_PRESET;
    }

    private effectiveFont(): string {
        const explicit = this.getAttribute("font");
        if (explicit) return explicit;
        return FONT_PRESETS[this.effectiveFontPresetKey()].stack;
    }

    /** Google-Fonts @import-URL voor het effectieve preset — alleen relevant zolang er
     * geen expliciet "font"-attribuut is (dat is per definitie geen preset). */
    private fontImportUrl(): string | null {
        if (this.getAttribute("font")) return null;
        return FONT_PRESETS[this.effectiveFontPresetKey()].importUrl;
    }

    /** Zelfde precedentie, voor popup vs inline. Widget-default is bewust "popup" (werkt
     * overal, plaatsonafhankelijk) — "inline" vereist dat de developer het script-tag op
     * de juiste plek in de pagina zet. */
    private effectiveMode(): "inline" | "popup" {
        const explicit = this.getAttribute("mode");
        if (explicit === "inline" || explicit === "popup") return explicit;
        if (this.fetchedWidgetMode === "inline" || this.fetchedWidgetMode === "popup") return this.fetchedWidgetMode;
        return "popup";
    }

    private buildCss(): string {
        const primary = this.effectivePrimary();
        const importUrl = this.fontImportUrl();
        const importRule = importUrl ? `@import url("${importUrl}");\n` : "";

        return importRule + widgetCss(
            primary,
            this.getAttribute("accent") || "#9e0625",
            this.effectiveBackground(),
            this.getAttribute("panel") || "#ffffff",
            this.getAttribute("text") || this.autoTextColor(),
            this.getAttribute("muted") || "#566166",
            this.getAttribute("border") || "#a9b4b9",
            this.effectiveFont(),
            this.getAttribute("button-text-color") || readableTextColor(primary)
        );
    }

    /** Haalt eenmalig (per company-slug) de huisstijl op (merkkleur, hoek, vorm,
     * knoptekst) — ook als sommige daarvan al als expliciet attribuut zijn meegegeven,
     * want de fetch levert de rest. De expliciete attributen blijven altijd winnen
     * (zie de effective*()-methodes hierboven). */
    private maybeFetchCompanyStyle() {
        const company = this.getAttribute("company");
        const apiBase = this.getAttribute("api-base");

        if (!company || !apiBase) return;
        if (this.styleFetchedFor === company) return; // al gedaan voor deze slug

        this.styleFetchedFor = company;

        getCompanyInfo(apiBase, company)
            .then((info) => {
                let changed = false;
                if (isHexColor(info.brandColor)) {
                    this.fetchedBrandColor = info.brandColor;
                    changed = true;
                }
                if (info.widgetPosition) {
                    this.fetchedWidgetPosition = info.widgetPosition;
                    changed = true;
                }
                if (info.widgetShape) {
                    this.fetchedWidgetShape = info.widgetShape;
                    changed = true;
                }
                if (info.widgetButtonText) {
                    this.fetchedWidgetButtonText = info.widgetButtonText;
                    changed = true;
                }
                if (isHexColor(info.widgetBackgroundColor)) {
                    this.fetchedWidgetBackground = info.widgetBackgroundColor;
                    changed = true;
                }
                if (info.widgetFont) {
                    this.fetchedWidgetFont = info.widgetFont;
                    changed = true;
                }
                if (info.widgetMode) {
                    this.fetchedWidgetMode = info.widgetMode;
                    changed = true;
                }
                if (changed) {
                    this.updateStyle();
                    this.renderReact();
                }
            })
            .catch(() => {
                // Stil falen: widget valt terug op de defaults.
            });
    }

    private updateStyle() {
        const style = this.shadow.querySelector("style");
        if (!style) return;

        style.textContent = this.buildCss();
    }

    private renderReact() {
        const company = this.getAttribute("company") || "";
        const apiBase = this.getAttribute("api-base") || "";
        const mode = this.effectiveMode();
        const lang = this.getAttribute("lang") || "nl";
        const buttonText = this.effectiveButtonText();
        const primary = this.effectivePrimary();
        const accent = this.getAttribute("accent") || "#9e0625";
        const buttonTextColor = this.getAttribute("button-text-color") || readableTextColor(primary);

        const shapeRadii = shapeToRadii(this.effectiveShape());
        const radius = this.getAttribute("radius") || shapeRadii.radius;
        const width = this.getAttribute("width") || "520px";
        const position = this.effectivePosition();
        const offsetX = this.getAttribute("offset-x") || "24px";
        const offsetY = this.getAttribute("offset-y") || "24px";

        const fabRadius = this.getAttribute("fab-radius") || shapeRadii.fabRadius;
        const fabWidth = this.getAttribute("fab-width") || "auto";
        const fabHeight = this.getAttribute("fab-height") || "auto";
        const fabPadding = this.getAttribute("fab-padding") || "12px 16px";
        const captchaToken = this.getAttribute("captcha-token") || undefined;

        if (!company || !apiBase) {
            this.mountEl.innerHTML = `
                <div style="font-family:system-ui;padding:12px;border:1px solid #fecaca;border-radius:12px;color:#991b1b;">
                    Missing required attribute(s): ${!company ? "company " : ""}${!apiBase ? "api-base" : ""}
                </div>
            `;
            return;
        }

        if (!this.reactRoot) {
            this.reactRoot = createRoot(this.mountEl);
        }

        this.reactRoot.render(
            <StrictMode>
                <Widget
                    companySlug={company}
                    apiBase={apiBase}
                    mode={mode}
                    lang={lang}
                    buttonText={buttonText}
                    primary={primary}
                    accent={accent}
                    buttonTextColor={buttonTextColor}
                    radius={radius}
                    width={width}
                    position={position}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    fabRadius={fabRadius}
                    fabWidth={fabWidth}
                    fabHeight={fabHeight}
                    fabPadding={fabPadding}
                    captchaToken={captchaToken}
                />
            </StrictMode>
        );
    }
}

if (!customElements.get("bookit-widget")) {
    customElements.define("bookit-widget", BookitWidgetElement);
}

export {};
